import { useState } from 'react';
import { Link } from 'react-router';

import { useAuth } from '../auth.jsx';
import { useT } from '../lib/i18n.js';

const KEY = 'ob_cookie_notice';

function dismissed(storage = globalThis.localStorage) {
  try {
    return storage?.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

/** A link that stays in the app for a path on this site and opens other sites in a new tab. */
export function SiteLink({ href, className, children }) {
  if (href.startsWith('/')) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" className={className}>
      {children}
    </a>
  );
}

/**
 * Octbill only sets essential cookies: the session (sign-in and CSRF protection), the language a
 * visitor picked (ob_lang) and the affiliate who referred them (ob_ref). None of them track people, so
 * no consent is needed and there is no opt-in wall: this is a note saying so, with a link to the
 * privacy page (general.privacy_url). "OK" hides it in this browser (localStorage). Staff can switch it
 * off (general.cookie_notice).
 */
export function CookieNotice() {
  const t = useT();
  const { legal } = useAuth() ?? {};
  const [hidden, setHidden] = useState(dismissed);
  if (hidden || !legal?.cookie_notice) return null;

  function dismiss() {
    try {
      globalThis.localStorage?.setItem(KEY, '1');
    } catch {
      // blocked storage: hidden for this page view only
    }
    setHidden(true);
  }

  return (
    <section
      aria-label={t('cookies.label')}
      className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-surface px-4 py-2 text-sm md:px-10"
    >
      <p className="min-w-0 flex-1">
        {t('cookies.body')}{' '}
        {legal.privacy_url ? (
          <SiteLink href={legal.privacy_url} className="font-semibold text-accent underline">
            {t('cookies.more')}
          </SiteLink>
        ) : null}
      </p>
      <button
        type="button"
        className="rounded-md border border-line-strong px-3 py-1 text-sm font-semibold hover:bg-sunk"
        onClick={dismiss}
      >
        {t('cookies.ok')}
      </button>
    </section>
  );
}
